import React, { useId, useRef } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';

const MAX_BYTES = 8 * 1024 * 1024;

type Props = {
  enabled: boolean;
  onEnabledChange: (checked: boolean) => void;
  previewDataUrl: string | null;
  fileName: string | null;
  onImageSelected: (dataUrl: string, fileName: string) => void;
  onClear: () => void;
  onError?: (message: string) => void;
  disabled?: boolean;
  enableLabel?: string;
  helperText?: string;
};

const SafetyAlertImageAttachmentField: React.FC<Props> = ({
  enabled,
  onEnabledChange,
  previewDataUrl,
  fileName,
  onImageSelected,
  onClear,
  onError,
  disabled,
  enableLabel = '사진 함께 전송',
  helperText = '켜면 선택한 이미지가 발송 이력에 함께 저장됨',
}) => {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      onError?.('이미지 파일(JPEG·PNG 등)만 선택 가능');
      return;
    }
    if (file.size > MAX_BYTES) {
      onError?.('이미지는 8MB 이하만 전송 가능');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string' && result.trim()) {
        onImageSelected(result, file.name);
      }
    };
    reader.onerror = () => onError?.('이미지를 읽지 못함');
    reader.readAsDataURL(file);
  };

  return (
    <Box sx={{ mt: 1.5 }}>
      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            onChange={(_e, checked) => onEnabledChange(checked)}
            disabled={disabled}
          />
        }
        label={
          <Box>
            <Typography variant="body2">{enableLabel}</Typography>
            <Typography variant="caption" color="text.secondary">
              {helperText}
            </Typography>
          </Box>
        }
        sx={{ alignItems: 'flex-start', ml: 0 }}
      />
      {enabled ? (
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ImageIcon />}
              disabled={disabled}
              onClick={() => inputRef.current?.click()}
            >
              사진 선택
            </Button>
            {previewDataUrl ? (
              <Button size="small" color="inherit" disabled={disabled} onClick={onClear}>
                선택 해제
              </Button>
            ) : null}
            {fileName ? (
              <Typography variant="caption" color="text.secondary">
                {fileName}
              </Typography>
            ) : (
              <Typography variant="caption" color="warning.main">
                전송하려면 사진을 선택
              </Typography>
            )}
          </Box>
          {previewDataUrl ? (
            <Box
              component="img"
              src={previewDataUrl}
              alt="첨부 미리보기"
              sx={{
                maxWidth: '100%',
                maxHeight: 200,
                objectFit: 'contain',
                borderRadius: 1,
                bgcolor: '#111',
              }}
            />
          ) : null}
          <input
            id={inputId}
            ref={inputRef}
            type="file"
            accept="image/*"
            hidden
            disabled={disabled}
            onChange={(e) => {
              handleFile(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default SafetyAlertImageAttachmentField;
